import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";

import { Button } from "@fawkes/ui";

import { AssignmentChipSection, type AssignmentItem } from "../components/AssignmentChipSection";

interface VestigingAssignment {
  vestigingId: number;
  naam: string;
  groupId: number | null;
  groupName: string | null;
  /** null | "no_group" | "ambiguous_group" | "shared_group" */
  blockedReason: string | null;
  /** Other branches the same group grants, or the competing group names — context for the reason. */
  sharedWith: string[];
}

interface VestigingAccessPayload {
  linked: boolean;
  fullAccess: boolean;
  assigned: VestigingAssignment[];
  available: VestigingAssignment[];
}

export interface UserVestigingenSectionProps {
  userId: string;
}

// Branch access for a user, shown as the branches themselves rather than the groups that carry them.
//
// Access is group-mediated in the shared docaserver schema (vestiging → vestiging_user_group →
// user_group → user_account_user_group), and the legacy docaserver app reads that same model. So
// "add a branch" really means "join the group that grants it" — the api owns that translation, and
// the group never surfaces here. Groups that grant no branch at all (the ~20 admin/permission groups
// like _dbapi_ or AlleenLezen) are therefore invisible on this screen by construction.
//
// The 1:1 branch↔group mapping is a convention, not a database constraint. Where it does not hold the
// api refuses the action and says why; those rows arrive with a blockedReason and render inert with
// the explanation rather than silently disappearing.
export function UserVestigingenSection({ userId }: UserVestigingenSectionProps) {
  const t = useTranslate();
  const {
    data,
    refetch,
    isError,
  } = useCustom<VestigingAccessPayload>({ url: `users/${userId}/vestigingen`, method: "get" });
  const { mutate, isLoading: isWriting } = useCustomMutation();

  const [error, setError] = useState<string | null>(null);

  // Validate the SHAPE, not just presence. A partial or unexpected body (a proxy error page, an older
  // api) would otherwise sail past a truthiness check and throw on .map() — which does not just break
  // this section, it unmounts the whole user screen with it.
  const raw = data?.data as Partial<VestigingAccessPayload> | undefined;
  const payload: VestigingAccessPayload | null =
    raw && Array.isArray(raw.assigned) && Array.isArray(raw.available)
      ? {
          linked: raw.linked !== false,
          fullAccess: Boolean(raw.fullAccess),
          assigned: raw.assigned,
          available: raw.available,
        }
      : null;

  const toItem = (v: VestigingAssignment): AssignmentItem<number> => ({
    id: v.vestigingId,
    label: v.naam,
    blockedReason: v.blockedReason,
  });

  function describeBlocked(item: AssignmentItem<number>): string {
    const row = [...(payload?.assigned ?? []), ...(payload?.available ?? [])].find(
      (v) => v.vestigingId === item.id,
    );
    const context = (row?.sharedWith ?? []).join(", ");
    switch (item.blockedReason) {
      case "no_group":
        return t("userDetailView.vestigingen.blocked.noGroup");
      case "ambiguous_group":
        return t("userDetailView.vestigingen.blocked.ambiguous").replace("%s", context);
      case "shared_group":
        return t("userDetailView.vestigingen.blocked.shared").replace("%s", context);
      default:
        return "";
    }
  }

  // Een gebruiker zonder docaserver-account kan geen vestiging krijgen; koppelen maakt dat account
  // (of koppelt het bestaande met dezelfde naam), daarna laat de sectie gewoon de vestigingen zien.
  function koppel() {
    setError(null);
    mutate(
      { url: `users/${userId}/legacy-koppeling`, method: "post", values: {} },
      {
        onSuccess: () => void refetch(),
        onError: () => setError(t("userDetailView.vestigingen.koppelenMislukt")),
      },
    );
  }

  function write(method: "post" | "delete", vestigingId: number) {
    setError(null);
    mutate(
      { url: `users/${userId}/vestigingen/${vestigingId}`, method, values: {} },
      {
        onSuccess: () => void refetch(),
        // The api refuses anything it cannot do unambiguously, so a failure here is a real refusal
        // worth surfacing verbatim rather than a lost click.
        onError: (err) => {
          const reason = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
          setError(
            reason === "shared_group" || reason === "ambiguous_group" || reason === "no_group"
              ? t("userDetailView.vestigingen.refused")
              : t("userDetailView.vestigingen.writeFailed"),
          );
        },
      },
    );
  }

  return (
    <AssignmentChipSection
      className="user-vestigingen"
      title={t("userDetailView.vestigingen.title")}
      help={t("userDetailView.vestigingen.help")}
      assigned={payload ? payload.assigned.map(toItem) : null}
      options={payload ? payload.available.map(toItem) : null}
      isError={isError}
      busy={isWriting}
      actionError={error}
      describeBlocked={describeBlocked}
      unavailable={
        payload && !payload.linked ? (
          <div className="user-vestigingen__koppelen">
            <p className="fawkes-muted">{t("userDetailView.vestigingen.notLinked")}</p>
            <p className="fawkes-muted">{t("userDetailView.vestigingen.koppelenUitleg")}</p>
            <Button variant="secondary" disabled={isWriting} onClick={koppel}>
              {t("userDetailView.vestigingen.koppelen")}
            </Button>
          </div>
        ) : undefined
      }
      notice={
        payload?.fullAccess ? (
          <p className="fawkes-muted" role="status">
            {t("userDetailView.vestigingen.fullAccess")}
          </p>
        ) : undefined
      }
      onAdd={(vestigingId) => write("post", vestigingId)}
      onRemove={(vestigingId) => write("delete", vestigingId)}
      labels={{
        loading: t("common.loading"),
        loadFailed: t("userDetailView.vestigingen.loadFailed"),
        empty: t("userDetailView.vestigingen.none"),
        add: t("userDetailView.vestigingen.add"),
        addDialogTitle: t("userDetailView.vestigingen.addTitle"),
        addDialogEmpty: t("userDetailView.vestigingen.addNoneLeft"),
        removeTitle: t("userDetailView.vestigingen.removeTitle"),
        removeBody: (label) => t("userDetailView.vestigingen.removeBody").replace("%s", label),
        removeConfirm: t("common.remove"),
        cancel: t("common.cancel"),
        removeAria: (label) => t("userDetailView.vestigingen.removeAria").replace("%s", label),
        searchPlaceholder: t("userDetailView.vestigingen.searchPlaceholder"),
        searchNoMatches: t("common.noMatches"),
      }}
    />
  );
}
